<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';

import PhotoCompareViewer from './PhotoCompareViewer.vue';

const props = defineProps({
  listing: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const PHOTO_SLOTS = [
  'front',
  'front_left',
  'left',
  'rear_left',
  'rear',
  'rear_right',
  'right',
  'front_right',
  'front_seats',
  'rear_seats',
  'dashboard',
  'odometer',
  'trunk',
  'engine',
];
const SIZE_LIMIT = 10 * 1024 * 1024;

const POLL_INTERVAL = 10000;

const uploadingSlot = ref('');
const dragOverSlot = ref('');
const regeneratingSlot = ref('');
// Slots where the dealer toggled the preview back to the original upload.
const originalViewSlots = ref({});

const photosBySlot = computed(() => {
  const map = {};
  (props.listing.photos || []).forEach(photo => {
    map[photo.slot] = photo;
  });
  return map;
});

const photoCount = computed(() => props.listing.photos?.length || 0);

// AI generation requires a live paid window (opened by publishing; expires
// 2 months later). Drafts and expired listings can't generate.
const withinPaidWindow = computed(() => {
  const expiresAt = props.listing.expires_at;
  return !!expiresAt && new Date(expiresAt).getTime() > Date.now();
});
const generationsUsed = computed(
  () => props.listing.photo_generations_used || 0
);
const generationsLimit = computed(
  () => props.listing.photo_generations_limit || 0
);
const allowanceLeft = computed(
  () => generationsUsed.value < generationsLimit.value
);

const isGenerating = slot =>
  photosBySlot.value[slot]?.generation_status === 'pending';
const generationFailed = slot =>
  photosBySlot.value[slot]?.generation_status === 'failed';
const hasGeneratedPhoto = slot =>
  !!photosBySlot.value[slot]?.generated_file_url;
const isShowingGenerated = slot =>
  hasGeneratedPhoto(slot) && !originalViewSlots.value[slot];
// The grid renders 14 tiles, so it loads the card-sized variant; full-size
// images only ever load inside the compare viewer.
const displayedPhotoUrl = slot => {
  const photo = photosBySlot.value[slot];
  return isShowingGenerated(slot) ? photo.generated_card_url : photo.card_url;
};

const viewerSlot = ref('');
const openViewer = slot => {
  viewerSlot.value = slot;
};

const regenerateTitle = computed(() => {
  if (!withinPaidWindow.value)
    return t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.ERRORS.NOT_PUBLISHED');
  if (!allowanceLeft.value)
    return t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.ERRORS.GENERATION_LIMIT');
  return t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.REGENERATE');
});

const generationSubmittedAgo = slot => {
  const submittedAt = photosBySlot.value[slot]?.generation_submitted_at;
  if (!submittedAt) return '';
  return dynamicTime(new Date(submittedAt).getTime() / 1000);
};

// Keep the listing fresh while generations are running so generated images
// and statuses appear without a manual reload.
const hasPendingGeneration = computed(() =>
  (props.listing.photos || []).some(
    photo => photo.generation_status === 'pending'
  )
);

let pollTimer = null;
watch(
  hasPendingGeneration,
  pending => {
    if (pending && !pollTimer) {
      pollTimer = setInterval(() => {
        store
          .dispatch('wgptCarListings/refresh', props.listing.id)
          .catch(() => {});
      }, POLL_INTERVAL);
    } else if (!pending && pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  },
  { immediate: true }
);
onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer);
});

const uploadPhoto = async (slot, file) => {
  if (!file) return;
  if (!file.type.startsWith('image/')) {
    useAlert(t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.ERRORS.FORMAT'));
    return;
  }
  if (file.size > SIZE_LIMIT) {
    useAlert(t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.ERRORS.SIZE'));
    return;
  }
  uploadingSlot.value = slot;
  try {
    await store.dispatch('wgptCarListings/attachPhoto', {
      id: props.listing.id,
      slot,
      file,
    });
  } catch (error) {
    useAlert(
      error?.message ||
        t('WGPT_CAR_LISTINGS.EDITOR.API.PHOTO_UPLOAD_ERROR_MESSAGE')
    );
  } finally {
    uploadingSlot.value = '';
  }
};

const handleFileChange = (slot, event) => {
  uploadPhoto(slot, event.target.files[0]);
  event.target.value = '';
};

const handleDrop = (slot, event) => {
  dragOverSlot.value = '';
  uploadPhoto(slot, event.dataTransfer.files[0]);
};

const handleRemove = async slot => {
  try {
    await store.dispatch('wgptCarListings/removePhoto', {
      id: props.listing.id,
      slot,
    });
  } catch (error) {
    useAlert(error?.message || t('WGPT_CAR_LISTINGS.EDITOR.API.ERROR_MESSAGE'));
  }
};

const handleSetPrimary = async slot => {
  try {
    await store.dispatch('wgptCarListings/update', {
      id: props.listing.id,
      primary_photo_slot: slot,
    });
  } catch (error) {
    useAlert(error?.message || t('WGPT_CAR_LISTINGS.EDITOR.API.ERROR_MESSAGE'));
  }
};

const handleRegenerate = async slot => {
  regeneratingSlot.value = slot;
  try {
    await store.dispatch('wgptCarListings/regeneratePhoto', {
      id: props.listing.id,
      slot,
    });
    originalViewSlots.value[slot] = false;
  } catch (error) {
    const errorMessages = {
      PHOTO_GENERATION_LIMIT_REACHED:
        'WGPT_CAR_LISTINGS.EDITOR.PHOTOS.ERRORS.GENERATION_LIMIT',
      LISTING_NOT_PUBLISHED:
        'WGPT_CAR_LISTINGS.EDITOR.PHOTOS.ERRORS.NOT_PUBLISHED',
    };
    const key =
      errorMessages[error?.response?.data?.error] ||
      'WGPT_CAR_LISTINGS.EDITOR.PHOTOS.ERRORS.GENERATION';
    useAlert(t(key));
  } finally {
    regeneratingSlot.value = '';
  }
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <p class="text-sm text-n-slate-11">
        {{ t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.HINT', { count: photoCount }) }}
      </p>
      <span
        v-if="withinPaidWindow && generationsLimit"
        class="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-11"
        :title="t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.AI_USAGE_HINT')"
      >
        <span class="block i-lucide-sparkles" />
        {{
          t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.AI_USAGE', {
            used: generationsUsed,
            limit: generationsLimit,
          })
        }}
      </span>
    </div>
    <div class="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
      <div
        v-for="slot in PHOTO_SLOTS"
        :key="slot"
        class="flex flex-col gap-1.5"
      >
        <div
          class="relative flex items-center justify-center overflow-hidden transition-colors border rounded-lg aspect-video group"
          :class="
            dragOverSlot === slot
              ? 'border-n-blue-8 bg-n-blue-2 border-dashed'
              : 'border-n-weak bg-n-alpha-1'
          "
          @dragover.prevent="dragOverSlot = slot"
          @dragleave.prevent="dragOverSlot = ''"
          @drop.prevent="handleDrop(slot, $event)"
        >
          <template v-if="photosBySlot[slot]">
            <img
              :src="displayedPhotoUrl(slot)"
              :alt="t(`WGPT_CAR_LISTINGS.PHOTO_SLOTS.${slot}`)"
              class="object-cover w-full h-full cursor-zoom-in"
              loading="lazy"
              decoding="async"
              @click="openViewer(slot)"
            />
            <span
              v-if="photosBySlot[slot].primary"
              class="absolute px-1.5 py-0.5 text-xs font-medium rounded top-1 start-1 bg-n-brand text-white"
            >
              {{ t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.PRIMARY') }}
            </span>
            <span
              v-if="isShowingGenerated(slot)"
              class="absolute inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium rounded top-1 end-1 bg-n-teal-9 text-white"
            >
              <span class="block i-lucide-sparkles" />
              {{ t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.GENERATED') }}
            </span>
            <div
              v-if="isGenerating(slot)"
              class="absolute inset-x-0 bottom-0 flex items-center gap-1.5 px-2 py-1 text-xs text-white bg-[#000000]/60"
            >
              <span class="block i-lucide-loader-circle animate-spin" />
              <span class="truncate">
                {{
                  t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.GENERATING', {
                    time: generationSubmittedAgo(slot),
                  })
                }}
              </span>
            </div>
            <div
              v-else-if="generationFailed(slot)"
              class="absolute inset-x-0 bottom-0 flex items-center gap-1.5 px-2 py-1 text-xs text-white bg-n-ruby-9/90"
            >
              <span class="block i-lucide-triangle-alert" />
              <span class="truncate">
                {{ t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.GENERATION_FAILED') }}
              </span>
            </div>
            <div
              class="absolute inset-0 flex items-center justify-center gap-2 transition-opacity opacity-0 cursor-zoom-in bg-[#000000]/50 group-hover:opacity-100"
              @click.self="openViewer(slot)"
            >
              <button
                type="button"
                class="p-1.5 text-white rounded-md hover:bg-white/20"
                :title="
                  hasGeneratedPhoto(slot)
                    ? t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.VIEWER.OPEN_COMPARE')
                    : t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.VIEWER.OPEN')
                "
                @click="openViewer(slot)"
              >
                <span
                  class="block"
                  :class="
                    hasGeneratedPhoto(slot)
                      ? 'i-lucide-git-compare'
                      : 'i-lucide-maximize-2'
                  "
                />
              </button>
              <button
                v-if="!photosBySlot[slot].primary"
                type="button"
                class="p-1.5 text-white rounded-md hover:bg-white/20"
                :title="t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.SET_PRIMARY')"
                @click="handleSetPrimary(slot)"
              >
                <span class="block i-lucide-star" />
              </button>
              <button
                type="button"
                class="p-1.5 text-white rounded-md hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed"
                :title="regenerateTitle"
                :disabled="
                  !withinPaidWindow ||
                  !allowanceLeft ||
                  isGenerating(slot) ||
                  regeneratingSlot === slot
                "
                @click="handleRegenerate(slot)"
              >
                <span
                  class="block"
                  :class="
                    regeneratingSlot === slot
                      ? 'i-lucide-loader-circle animate-spin'
                      : 'i-lucide-sparkles'
                  "
                />
              </button>
              <label
                class="p-1.5 text-white rounded-md cursor-pointer hover:bg-white/20"
                :title="t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.REPLACE')"
              >
                <span class="block i-lucide-refresh-cw" />
                <input
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="handleFileChange(slot, $event)"
                />
              </label>
              <button
                type="button"
                class="p-1.5 text-white rounded-md hover:bg-white/20"
                :title="t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.REMOVE')"
                @click="handleRemove(slot)"
              >
                <span class="block i-lucide-trash-2" />
              </button>
            </div>
          </template>
          <label
            v-else
            class="flex flex-col items-center justify-center w-full h-full gap-1 cursor-pointer text-n-slate-10 hover:text-n-slate-12"
          >
            <span
              class="block text-lg"
              :class="
                uploadingSlot === slot
                  ? 'i-lucide-loader-circle animate-spin'
                  : 'i-lucide-image-plus'
              "
            />
            <span class="text-xs">
              {{ t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.UPLOAD') }}
            </span>
            <input
              type="file"
              accept="image/*"
              class="hidden"
              :disabled="!!uploadingSlot"
              @change="handleFileChange(slot, $event)"
            />
          </label>
        </div>
        <span class="text-xs text-center text-n-slate-11">
          {{ t(`WGPT_CAR_LISTINGS.PHOTO_SLOTS.${slot}`) }}
        </span>
        <div v-if="hasGeneratedPhoto(slot)" class="flex justify-center">
          <div
            class="inline-flex overflow-hidden text-xs border rounded-md border-n-weak"
          >
            <button
              type="button"
              class="px-2 py-0.5 transition-colors"
              :class="
                isShowingGenerated(slot)
                  ? 'bg-n-teal-9 text-white'
                  : 'text-n-slate-11 hover:bg-n-alpha-1'
              "
              @click="originalViewSlots[slot] = false"
            >
              {{ t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.GENERATED') }}
            </button>
            <button
              type="button"
              class="px-2 py-0.5 transition-colors"
              :class="
                isShowingGenerated(slot)
                  ? 'text-n-slate-11 hover:bg-n-alpha-1'
                  : 'bg-n-slate-9 text-white'
              "
              @click="originalViewSlots[slot] = true"
            >
              {{ t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.ORIGINAL') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <PhotoCompareViewer
      v-if="viewerSlot"
      :photos="listing.photos"
      :initial-slot="viewerSlot"
      :can-regenerate="withinPaidWindow && allowanceLeft"
      :regenerate-title="regenerateTitle"
      @set-primary="handleSetPrimary"
      @regenerate="handleRegenerate"
      @close="viewerSlot = ''"
    />
  </div>
</template>
